import { isCalendarVisibleInSidebar } from '@/utils/calendar/display'
import { useUserStore } from '@/store/userStore'
import { CalendarsService, UsersService, type CustomerCalendar } from '@/types/generated'
import { computed, onMounted, ref, watch } from 'vue'

/**
 * Connected appointment calendars for the current org (API v3).
 */
export function useConnectedCalendars() {
  const userStore = useUserStore()
  const calendars = ref<CustomerCalendar[]>([])
  const isLoading = ref(false)
  const loadError = ref<string | null>(null)
  const selectedCalendarUuid = ref<string | null>(null)
  let persistRequestSeq = 0

  const visibleCalendars = computed(() => calendars.value.filter(isCalendarVisibleInSidebar))

  const hasCalendars = computed(() => visibleCalendars.value.length > 0)

  const resolvePreferredCalendarUuid = (list: CustomerCalendar[]): string | null => {
    if (list.length === 0) {
      return null
    }

    const focusedCalendarUuid = userStore.user?.ui_state?.focused_calendar_uuid
    if (focusedCalendarUuid && list.some((calendar) => calendar.uuid === focusedCalendarUuid)) {
      return focusedCalendarUuid
    }

    const primaryCalendarUuid = list.find((calendar) => calendar.calendar_primary)?.uuid
    if (primaryCalendarUuid) {
      return primaryCalendarUuid
    }

    return list[0]?.uuid ?? null
  }

  const persistFocusedCalendar = async (uuid: string | null): Promise<void> => {
    if (!uuid || !userStore.user?.uuid) {
      return
    }

    if (userStore.user.ui_state?.focused_calendar_uuid === uuid) {
      return
    }

    const nextUiState = {
      ...userStore.user.ui_state,
      focused_calendar_uuid: uuid
    }
    const requestSeq = ++persistRequestSeq

    try {
      await UsersService.usersPartialUpdate({
        uuid: userStore.user.uuid,
        requestBody: {
          ui_state: nextUiState
        }
      })

      if (requestSeq !== persistRequestSeq) {
        return
      }

      userStore.user.ui_state = nextUiState
    } catch (error) {
      console.error('[calendar] persistFocusedCalendar failed', error)
      // Keep the local selection even if persisting the preference fails.
    }
  }

  const loadCalendars = async (): Promise<void> => {
    isLoading.value = true
    loadError.value = null
    try {
      const response = await CalendarsService.calendarsList({ limit: 200 })
      calendars.value = (response.results ?? []) as CustomerCalendar[]
    } catch (error) {
      console.error('[calendar] loadCalendars failed', error)
      calendars.value = []
      loadError.value = 'load_failed'
    } finally {
      isLoading.value = false
    }
  }

  watch(visibleCalendars, (list) => {
    if (list.length === 0) {
      selectedCalendarUuid.value = null
      return
    }

    const stillSelected = list.some((calendar) => calendar.uuid === selectedCalendarUuid.value)
    if (!stillSelected) {
      selectedCalendarUuid.value = resolvePreferredCalendarUuid(list)
    }
  })

  watch(selectedCalendarUuid, (uuid) => {
    void persistFocusedCalendar(uuid)
  })

  onMounted(() => {
    void (async () => {
      if (!userStore.user) {
        await userStore.fetchUser()
      }

      await loadCalendars()
    })()
  })

  return {
    calendars,
    visibleCalendars,
    hasCalendars,
    selectedCalendarUuid,
    isLoading,
    loadError,
    loadCalendars,
    selectCalendar: (uuid: string) => {
      selectedCalendarUuid.value = uuid
    }
  }
}
